import React, { FC, useEffect, useState } from 'react';
import { FormControlLabel, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import { AuthenticationState } from '@pruforce/common-adminweb-sdk';
import { isEmpty, isEqual, isObject } from 'lodash';
import { FormInstance } from 'rc-field-form';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import Form from 'src/app/common/components/Form';
import { Routes, SPECIFIC_ROUTES } from 'src/app/common/constants';
import SpecificEventDeeplink from 'src/app/common/components/SpecificDeeplinkList/event/index';
import SpecificSurveyDeeplinkList from 'src/app/common/components/SpecificDeeplinkList/survey/index';
import SpecificTrainingDeeplink from 'src/app/common/components/SpecificDeeplinkList/training';
import { CampaignListComponent } from 'src/app/common/components/SpecificDeeplinkList/campaign/campaign.component';
import { SpecificArticleDeepLink } from 'src/app/modules/News/pages/Article/List/components/specific-article-deeplink';
import { CampaignItem } from 'src/app/modules/PulseLeads/types/campaign-types';
import { getCurrentConfig } from 'src/app/common/utils';
import { RootState } from 'src/redux/store';
import { KeyTaskConfiguration, KeyTaskLinkParams, OpenByTypeEnum, RedirectTypeEnum } from '../../../../types/key-task-summarizer-type';
import { OpenByType } from '../../../../types/operation-type';
import { ConfigurationRules } from '../keyTaskSummarizerDetailForm.rules';

type ManagementMessageFieldsProps = {
  form: FormInstance;
  isDisabled: boolean;
  initialLink: string;
  initialLinkParams?: KeyTaskLinkParams | null;
  configurationRules: (field: keyof KeyTaskConfiguration) => ConfigurationRules;
  onChangeFields: (fields: Partial<KeyTaskConfiguration>) => void;
};

type AppRoute = string | { link: string; params: Record<string, unknown> };

const ManagementMessageFields: FC<ManagementMessageFieldsProps> = ({
  form,
  isDisabled,
  initialLink,
  initialLinkParams,
  configurationRules,
  onChangeFields,
}) => {
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);
  const AppRoutes = (getCurrentConfig(user)?.AppRoutes || {}) as Record<string, AppRoute>;
  const [deeplink, setDeepLink] = useState('');
  const [, setSelectedCampaign] = useState<CampaignItem>();

  useEffect(() => {
    if (!initialLinkParams || isEmpty(initialLinkParams) || SPECIFIC_ROUTES.includes(initialLink)) {
      setDeepLink(initialLink);
    } else {
      setDeepLink(JSON.stringify({ link: initialLink, params: initialLinkParams }));
    }
  }, [initialLink, initialLinkParams]);

  const shouldDisplaySpecificLink = (link: string) =>
    [
      Routes.SPECIFIC_EVENT_LINK,
      Routes.SPECIFIC_TRAINING_LINK,
      Routes.SPECIFIC_SURVEY_LINK,
      Routes.SPECIFIC_CAMPAIGN_LINK,
      Routes.NEWS_DETAIL,
    ].includes(link as Routes);

  const specificLinkRules = [
    {
      validator: () => {
        const link = form.getFieldValue('link');
        const params = (form.getFieldValue('params') as KeyTaskLinkParams | null)?.linkParams;
        if (
          form.getFieldValue('redirectTo') === RedirectTypeEnum.APP &&
          shouldDisplaySpecificLink(link) &&
          !params?.id &&
          !params?.eventId &&
          !params?.campaignId &&
          !params?.newsId
        ) {
          return Promise.reject(new Error(Translation('component.form-required-text')));
        }
        return Promise.resolve();
      },
    },
  ];

  const onSelectDeeplinkItem = (selectedId: string | undefined, idName = 'id') => {
    onChangeFields({ params: { linkParams: { [idName]: selectedId } } });
    form.setFieldsValue({ linkParamsField: selectedId });
  };

  const onSelectCampaignItem = (campaign: CampaignItem) => {
    onSelectDeeplinkItem(campaign._id, 'campaignId');
    setSelectedCampaign(campaign);
  };

  const onSelectRoute = (value: string) => {
    let selectedValue: unknown = value;
    try {
      selectedValue = JSON.parse(value);
    } catch {
      selectedValue = value;
    }

    const selectedRoute = Object.entries(AppRoutes).find(([, route]) => {
      if (typeof route === 'string') return route === selectedValue;
      return (
        isObject(selectedValue) &&
        route.link === (selectedValue as { link?: string }).link &&
        isEqual(route.params, (selectedValue as { params?: object }).params)
      );
    });

    if (selectedRoute && typeof selectedRoute[1] !== 'string') {
      onChangeFields({ link: selectedRoute[1].link, params: selectedRoute[1].params });
      form.setFieldsValue({ linkParamsField: '' });
      setDeepLink(JSON.stringify(selectedRoute[1]));
    } else {
      onChangeFields({ link: value, params: null });
      form.setFieldsValue({ linkParamsField: '' });
      setDeepLink(value);
    }
  };

  return (
    <>
      <Form.Item
        name="redirectTo"
        label={Translation('component.formLabel.redirectTo')}
        rules={configurationRules('redirectTo')}
      >
        <RadioGroup
          row
          aria-label={Translation('component.formLabel.redirectTo')}
          onChange={(event) => {
            onChangeFields({
              redirectTo: event.target.value as RedirectTypeEnum,
              link: '',
              params: null,
              openBy: '',
              canShare: '',
            });
            form.setFieldsValue({ linkParamsField: '' });
            setDeepLink('');
          }}
        >
          <FormControlLabel
            value={RedirectTypeEnum.NO}
            control={<Radio disabled={isDisabled} />}
            label={Translation('component.formSelectItem.no-link')}
          />
          <FormControlLabel
            value={RedirectTypeEnum.APP}
            control={<Radio disabled={isDisabled} />}
            label={Translation('component.formSelectItem.page-in-app')}
          />
          <FormControlLabel
            value={RedirectTypeEnum.EXTERNAL}
            control={<Radio disabled={isDisabled} />}
            label={Translation('component.formSelectItem.external-url')}
          />
        </RadioGroup>
      </Form.Item>
      <Form.Item noStyle shouldUpdate={(previous, current) => previous.redirectTo !== current.redirectTo}>
        {(_, meta, { getFieldValue }) => {
          const redirectType = getFieldValue('redirectTo');
          if (redirectType === RedirectTypeEnum.APP) {
            return (
              <>
                <Form.Item
                  name="link"
                  label={Translation('component.formLabel.url')}
                  rules={configurationRules('link')}
                >
                  {() => (
                    <TextField
                      disabled={isDisabled}
                      select
                      margin="dense"
                      variant="outlined"
                      value={deeplink}
                      fullWidth
                      onChange={(event) => onSelectRoute(event.target.value)}
                      inputProps={{ 'aria-label': Translation('component.formLabel.url') }}
                    >
                      {Object.entries(AppRoutes)
                        .filter(([label]) => label !== 'route.pruleads.specific.campaign.link')
                        .map(([label, route]) => {
                          let value = typeof route === 'string' ? route : JSON.stringify(route);
                          if (value.startsWith(Routes.PRUACTION) && value.split('/').length > 1) {
                            value = `${window.envConfig['AGENT_PORTAL_HOST']}/${value}`;
                          }
                          return (
                            <MenuItem key={value} value={value}>
                              {Translation(label)}
                            </MenuItem>
                          );
                        })}
                    </TextField>
                  )}
                </Form.Item>
                <Form.Item
                  name="linkParamsField"
                  label=" "
                  hideDot
                  rules={specificLinkRules}
                  shouldUpdate={(previous, current) => previous.link !== current.link}
                >
                  {(_: unknown, __: unknown, { getFieldValue: getValue }: { getFieldValue: (name: string) => string }) => {
                    const link = getValue('link');
                    return (
                      shouldDisplaySpecificLink(link) && (
                        <div>
                          {link === Routes.SPECIFIC_EVENT_LINK && (
                            <SpecificEventDeeplink
                              isView={isDisabled}
                              selectId={initialLinkParams?.linkParams?.eventId || ''}
                              onSelectItem={(selectedId: string) => onSelectDeeplinkItem(selectedId, 'eventId')}
                            />
                          )}
                          {link === Routes.SPECIFIC_TRAINING_LINK && (
                            <SpecificTrainingDeeplink
                              isView={isDisabled}
                              selectId={initialLinkParams?.linkParams?.id || ''}
                              onSelectItem={(selectedId: string) => onSelectDeeplinkItem(selectedId)}
                            />
                          )}
                          {link === Routes.SPECIFIC_SURVEY_LINK && (
                            <SpecificSurveyDeeplinkList
                              isView={isDisabled}
                              selectId={initialLinkParams?.linkParams?.id || ''}
                              onSelectItem={(selectedId: string) => onSelectDeeplinkItem(selectedId)}
                            />
                          )}
                          {link === Routes.SPECIFIC_CAMPAIGN_LINK && (
                            <CampaignListComponent
                              isView={isDisabled}
                              selectId={initialLinkParams?.linkParams?.campaignId || ''}
                              onSelectItem={onSelectCampaignItem}
                              onChange={setSelectedCampaign}
                            />
                          )}
                          {link === Routes.NEWS_DETAIL && (
                            <SpecificArticleDeepLink
                              isView={isDisabled}
                              selectId={initialLinkParams?.linkParams?.newsId || ''}
                              onSelectItem={(selectedId) => onSelectDeeplinkItem(selectedId, 'newsId')}
                              allAgentOnly={true}
                            />
                          )}
                        </div>
                      )
                    );
                  }}
                </Form.Item>
              </>
            );
          }
          if (redirectType === RedirectTypeEnum.EXTERNAL) {
            return (
              <>
                <Form.Item
                  name="link"
                  label={Translation('component.formLabel.url')}
                  rules={configurationRules('link')}
                >
                  {(control) => (
                    <TextField
                      disabled={isDisabled}
                      value={control.value}
                      onChange={(event) => {
                        control.onChange(event.target.value);
                      }}
                      margin="dense"
                      variant="outlined"
                      fullWidth
                      inputProps={{ 'aria-label': Translation('component.formLabel.url') }}
                    />
                  )}
                </Form.Item>
                <Form.Item
                  name="openBy"
                  label={Translation('component.formLabel.open-by')}
                  rules={configurationRules('openBy')}
                >
                  <RadioGroup row aria-label="openBy" name="openBy">
                    {Object.entries(OpenByType).map(([label, value]) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={<Radio disabled={isDisabled} />}
                        label={Translation(`component.formSelectItem.${label}`)}
                      />
                    ))}
                  </RadioGroup>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(previous, current) => previous.openBy !== current.openBy}>
                  {(_, __, { getFieldValue: getValue }) =>
                    getValue('openBy') === OpenByTypeEnum.WEBVIEW ? (
                      <Form.Item
                        name="canShare"
                        label={Translation('component.formLabel.can-be-shared')}
                        rules={configurationRules('canShare')}
                        getValueFromEvent={(event: React.ChangeEvent<HTMLInputElement>) => event.target.value === 'true'}
                      >
                        <RadioGroup row aria-label="canShare" name="canShare">
                          <FormControlLabel
                            value={true}
                            control={<Radio disabled={isDisabled} />}
                            label={Translation('app.select.yes')}
                          />
                          <FormControlLabel
                            value={false}
                            control={<Radio disabled={isDisabled} />}
                            label={Translation('app.select.no')}
                          />
                        </RadioGroup>
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </>
            );
          }
          return null;
        }}
      </Form.Item>
    </>
  );
};

export default ManagementMessageFields;
